import gql from 'graphql-tag';
import { useRef } from 'react';

import { InducteeEssayFragment } from '@graphqlTypes';
import { ACFMediaFragment } from '@/fragments';
import { Button, Container, Image, ImageAttribution, RevealWrapper } from '@components';
import { usePageWrapperData } from '@/providers/PageWrapperContext';

import { getLinkData } from '@/core/utils';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView, motion } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from './InducteeEssay.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

export default function InducteeEssay(props: InducteeEssayFragment) {
  const { eyebrow, headline, attribution, image, imageAttribution, cta } = props;
  const { labels } = usePageWrapperData();
  const { hofEssayCta } = labels || {};
  const ctaOpts = { ...cta, label: cta?.label || hofEssayCta };
  const ctaData = getLinkData(ctaOpts);

  // -------------------- Animation

  const rootEl = useRef<HTMLElement | null>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Render

  return (
    <section className={cx(["inductee-essay"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container className={cx(["content"])}>

          <div className={cx(["content-side"])}>
            <div className={cx(["image-container"])}>
              <Image image={image} />
              { imageAttribution && (
                <ImageAttribution
                  className={cx(["image-attribution"])}
                  text={imageAttribution}
                />
              )}
            </div>
          </div>

          <div className={cx(["content-side"])}>

            <div className={cx(["copy"])}>
              <p className={cx(["highlighted-text", "eyebrow-l"])}>{eyebrow}</p>
              <p className={cx(["headline", "title-lc-s"])}>{headline}</p>
              <p className={cx(["eyebrow-l", "attribution"])}>{attribution}</p>
              {ctaData && (
                <Button className={cx(["button"])} {...ctaData} />
              )}
            </div>
          </div>

        </Container>
      </RevealWrapper>
    </section>
  );

}

InducteeEssay.fragments = {
  entry: gql`
    ${ACFMediaFragment}
    fragment InducteeEssay on InducteeDataImpactFlexibleContentHallOfFameEssayLayout {
      eyebrow
      headline
      attribution
      imageAttribution
      image {
        ...ACFMedia
      }
      cta {
        linkType
        label
        externalLink
        page {
          node {
            uri
          }
        }
      }
    }
  `,
};
